import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { resolveTemplate } from '../lib/template';
import type { TemplateFillState } from './prompts-view-types';

interface TemplateFillDialogProps {
  state: TemplateFillState;
  onClose: () => void;
  onInsert: (resolvedPrompt: string) => Promise<void>;
}

export default function TemplateFillDialog({
  state,
  onClose,
  onInsert,
}: Readonly<TemplateFillDialogProps>): JSX.Element {
  const [values, setValues] = useState(state.values);

  useEffect(() => {
    setValues(state.values);
  }, [state]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onInsert(resolveTemplate(state.prompt.content, values));
  };

  return (
    <Dialog open>
      <DialogOverlay onClick={onClose} />
      <DialogContent role="dialog" aria-modal="true" aria-label="Fill in template variables">
        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Fill In Template</DialogTitle>
          </DialogHeader>

          {state.variables.map((variable) => {
            const fieldId = `template-${variable.name}`;
            return (
              <div className="flex flex-col gap-[0.35rem]" key={variable.name}>
                <Label htmlFor={fieldId}>{variable.displayName}</Label>
                <Input
                  id={fieldId}
                  value={values[variable.name] ?? ''}
                  placeholder={variable.hint}
                  onChange={(event) =>
                    setValues((previous) => ({
                      ...previous,
                      [variable.name]: event.target.value,
                    }))
                  }
                />
              </div>
            );
          })}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={state.variables.some((variable) => !(values[variable.name] ?? '').trim())}
            >
              Insert
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
